// CHỌN VIDEO KẾ TIẾP cho bước "Đăng video" (06/10/2026) — logic THUẦN dùng chung Boss (máy chủ cấp phát, trong giao dịch) và unit test.
//
// Quy tắc (người dùng duyệt thiết kế 06/10):
//  1. Mỗi cặp (video, bot, nền tảng) chỉ được đăng ĐÚNG MỘT LẦN, mãi mãi: có hàng đang giữ / đã đăng / chưa xác nhận thì cặp đó KHÔNG BAO GIỜ được chọn lại
//     (chỉ người dùng bấm "Cho phép đăng lại" mới mở ra). Hàng "lỗi chưa đăng" (chưa bấm Đăng) mở lại được, nhưng quá MAX_LOI_MOT_CAP lần thì bỏ video đó cho bot đó.
//  2. Hàng đợi theo bot: lan_luot (cũ nhất trước) / ngau_nhien / theo_nhan (lọc nhãn, rồi cũ nhất trước).
//  3. Nhiều bot cùng kho: khongCungNgay (video bot khác đã nhận trong 24 giờ thì bỏ qua) và moiVideoMotBot (video đã thuộc bot khác thì bỏ qua mãi).
//  4. Hết video → kết quả "het_video" (KHÔNG đăng lại, KHÔNG phải lỗi hệ thống).
//  7. Trần ngày (đếm hàng giữ chỗ/đã đăng/chưa xác nhận trong ngày giờ Việt Nam) + khoảng chờ giữa hai lần (ngẫu nhiên tất định trong [tu, den] phút).
import type { NenTangVideo } from "./video-kho";
import { lyDoKhongHop, type KieuDangYeuCau, type MetaVideo } from "./video-kho";

export type CheDoChon = "lan_luot" | "ngau_nhien" | "theo_nhan";
export const CHE_DO_CHON: readonly CheDoChon[] = ["lan_luot", "ngau_nhien", "theo_nhan"];
export const laCheDoChon = (x: unknown): x is CheDoChon => x === "lan_luot" || x === "ngau_nhien" || x === "theo_nhan";

export type TrangThaiDang = "dang_dang" | "da_dang" | "chua_xac_nhan" | "loi" | "huy";
/** Trạng thái CHIẾM cặp (video, bot, nền tảng): không chọn lại, tính vào trần ngày. */
export const TT_CHIEM: readonly TrangThaiDang[] = ["dang_dang", "da_dang", "chua_xac_nhan"];
export const chiemCap = (t: string): boolean => (TT_CHIEM as readonly string[]).includes(t);
export const MAX_LOI_MOT_CAP = 2;
/** Hàng "đang giữ" quá hạn này mà bot chưa báo kết quả → coi là CHƯA XÁC NHẬN (không tự đăng lại). */
export const HAN_GIU_CHO_MS = 3 * 3600_000;

export interface VideoUngVien { id: string; taoLuc: string; nhan: string[]; kichThuoc: number; thoiLuong: number | null; rong: number | null; cao: number | null }
export interface BanGhiDang { videoId: string; botId: string; nenTang: string; trangThai: TrangThaiDang | string; luc: string }

export interface YeuCauKeTiep {
  botId: string;
  nenTang: NenTangVideo;
  cheDo: CheDoChon;
  nhan?: string;
  khongCungNgay: boolean;
  moiVideoMotBot: boolean;
  /** 0 = không giới hạn. */
  tranNgay: number;
  choTuPhut: number;
  choDenPhut: number;
  /** Chỉ video này (nút "Đăng ngay…"); vẫn tuân luật cặp / trần / khoảng chờ. */
  videoId?: string;
  /**
   * Tự xoá tệp BẬT (06/10/2026 — "mỗi video chỉ đăng MỘT lần ở MỘT bot"): các video đã có bot / nền tảng KHÁC nhận hoặc đăng (kể cả đang đăng) → bỏ qua, không cấp cho bot này.
   * Không áp dụng khi người dùng chọn đích danh video (videoId — "Đăng ngay…"). Máy chủ KHÔNG đưa video bật "giữ lại cho nhiều bot" vào tập này.
   */
  daCoNoiKhac?: ReadonlySet<string>;
  kieuDang?: KieuDangYeuCau;
  bayGio: Date;
  rnd: () => number;
}

export type KetQuaKeTiep =
  | { ketQua: "chon"; video: VideoUngVien; ghiChu: string }
  | { ketQua: "het_video"; ly: string; tong: number; daDangBot: number; khongHop: number; khacNhan: number; boKhac: number; loiNhieuLan: number }
  | { ketQua: "het_tran_ngay"; daDang: number; tran: number; ly: string }
  | { ketQua: "chua_den_gio"; denLuc: string; ly: string }
  | { ketQua: "khong_co_video"; ly: string };

const PHUT = 60_000;
/** Ngày theo giờ Việt Nam (UTC+7) dạng YYYY-MM-DD. */
export function ngayVN(d: Date): string { return new Date(d.getTime() + 7 * 3600_000).toISOString().slice(0, 10); }

/** Băm chữ → số 0..1 tất định (FNV-1a + bước trộn cuối kiểu murmur3 để chuỗi giống nhau chỉ khác ký tự cuối vẫn ra số cách xa nhau) — "ngẫu nhiên" ổn định theo hạt giống. */
export function bamThanhSo(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b) >>> 0; h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35) >>> 0; h ^= h >>> 16;
  return (h >>> 0) / 0x100000000;
}

/** Khoảng chờ yêu cầu giữa hai lần đăng (phút): tất định theo hạt giống trong [tu, den]; den < tu → lấy tu; âm / không phải số → 0. */
export function khoangChoPhut(seed: string, tu: number, den: number): number {
  const a = Number.isFinite(tu) && tu > 0 ? tu : 0;
  const b = Number.isFinite(den) && den > a ? den : a;
  return a + bamThanhSo(seed) * (b - a);
}

/** Độ LỆCH GIỜ ngẫu nhiên (phút, nguyên) trước khi bắt đầu đăng — nhiều bot cùng lịch không đăng đồng loạt. rnd ∈ [0,1). tối đa ≤ 0 → 0. */
export function lechGioPhut(rnd: () => number, toiDaPhut: number): number {
  if (!Number.isFinite(toiDaPhut) || toiDaPhut <= 0) return 0;
  const x = rnd();
  const t = Number.isFinite(x) ? Math.min(0.999999, Math.max(0, x)) : 0;
  return Math.floor(t * (Math.floor(toiDaPhut) + 1));
}

const coNhan = (v: VideoUngVien, nhan: string): boolean => v.nhan.some((x) => x.toLocaleLowerCase("vi") === nhan.toLocaleLowerCase("vi"));

/**
 * Chọn video kế tiếp. `video` = mọi video SẴN SÀNG của chủ; `lichSu` = mọi hàng đăng của chủ (mọi bot) — hàm tự lọc theo bot / nền tảng.
 * Không đổi đầu vào, không ghi gì: máy chủ ghi hàng giữ chỗ trong CÙNG giao dịch với lời gọi này (xem server/src/kho-video.ts).
 */
export function chonVideoKeTiep(video: readonly VideoUngVien[], lichSu: readonly BanGhiDang[], y: YeuCauKeTiep): KetQuaKeTiep {
  const bay = y.bayGio.getTime();
  const cua = lichSu.filter((l) => l.nenTang === y.nenTang);
  const cuaBot = cua.filter((l) => l.botId === y.botId);
  const chiem = cuaBot.filter((l) => chiemCap(l.trangThai));

  // trần ngày
  if (y.tranNgay > 0) {
    const homNay = ngayVN(y.bayGio);
    const dem = chiem.filter((l) => ngayVN(new Date(l.luc)) === homNay).length;
    if (dem >= y.tranNgay) return { ketQua: "het_tran_ngay", daDang: dem, tran: y.tranNgay, ly: `đã đủ ${dem}/${y.tranNgay} video hôm nay cho bot này trên ${y.nenTang} — mai đăng tiếp (giữ nhịp an toàn)` };
  }
  // khoảng chờ giữa hai lần
  if (y.choTuPhut > 0 || y.choDenPhut > 0) {
    const cuoi = chiem.map((l) => Date.parse(l.luc)).filter((n) => Number.isFinite(n)).sort((a, b) => b - a)[0];
    if (cuoi !== undefined) {
      const can = khoangChoPhut(`${y.botId}|${y.nenTang}|${cuoi}`, y.choTuPhut, y.choDenPhut);
      const den = cuoi + can * PHUT;
      if (bay < den) return { ketQua: "chua_den_gio", denLuc: new Date(den).toISOString(), ly: `chưa đủ thời gian chờ giữa hai lần đăng (cần khoảng ${Math.round(can)} phút kể từ lần trước) — lần này bỏ qua, không đăng` };
    }
  }

  const cap = new Set(chiem.map((l) => l.videoId)); // cặp đã chiếm bởi CHÍNH bot này
  const dem24h = 24 * 3600_000;
  const biBotKhac = new Set<string>();
  const biBotKhac24h = new Set<string>();
  for (const l of cua) {
    if (l.botId === y.botId || !chiemCap(l.trangThai)) continue;
    biBotKhac.add(l.videoId);
    const t = Date.parse(l.luc);
    if (Number.isFinite(t) && bay - t < dem24h) biBotKhac24h.add(l.videoId);
  }
  const soLoi = new Map<string, number>();
  for (const l of cuaBot) if (l.trangThai === "loi") soLoi.set(l.videoId, (soLoi.get(l.videoId) ?? 0) + 1);

  let daDangBot = 0, khongHop = 0, khacNhan = 0, boKhac = 0, loiNhieuLan = 0, noiKhac = 0;
  const lyDoKhongHopDau: string[] = [];
  let ung: VideoUngVien[] = [];
  const lamGi = y.videoId ? video.filter((v) => v.id === y.videoId) : video;
  if (y.videoId && !lamGi.length) return { ketQua: "khong_co_video", ly: "video này không còn trong kho (đã xoá?)" };
  for (const v of lamGi) {
    if (cap.has(v.id)) { daDangBot++; continue; }
    if ((soLoi.get(v.id) ?? 0) >= MAX_LOI_MOT_CAP) { loiNhieuLan++; continue; }
    if (y.cheDo === "theo_nhan" && !y.videoId && (!y.nhan || !coNhan(v, y.nhan))) { khacNhan++; continue; }
    if (!y.videoId && y.daCoNoiKhac?.has(v.id)) { noiKhac++; continue; }
    if (y.moiVideoMotBot && biBotKhac.has(v.id)) { boKhac++; continue; }
    if (y.khongCungNgay && biBotKhac24h.has(v.id)) { boKhac++; continue; }
    const m: MetaVideo = { thoiLuong: v.thoiLuong, rong: v.rong, cao: v.cao };
    const ly = lyDoKhongHop(y.nenTang, m, v.kichThuoc, y.kieuDang ?? "tu_nhan");
    if (ly) { khongHop++; if (lyDoKhongHopDau.length < 1) lyDoKhongHopDau.push(ly); continue; }
    ung.push(v);
  }
  if (!ung.length) {
    const phan: string[] = [];
    if (daDangBot) phan.push(`${daDangBot} video bot này đã đăng rồi (không đăng lại)`);
    if (boKhac) phan.push(`${boKhac} video đã/đang thuộc bot khác`);
    if (noiKhac) phan.push(`${noiKhac} video đã đăng ở bot/nền tảng khác (mỗi video chỉ đăng MỘT lần, tệp đã xoá hoặc sắp xoá khỏi máy chủ)`);
    if (khacNhan) phan.push(`${khacNhan} video không có nhãn “${y.nhan ?? ""}”`);
    if (khongHop) phan.push(`${khongHop} video không hợp nền tảng (${lyDoKhongHopDau[0] ?? ""})`);
    if (loiNhieuLan) phan.push(`${loiNhieuLan} video lỗi ${MAX_LOI_MOT_CAP} lần nên bỏ`);
    return {
      ketQua: "het_video", tong: video.length, daDangBot, khongHop, khacNhan, boKhac: boKhac + noiKhac, loiNhieuLan,
      ly: video.length ? `Hết video chưa đăng cho bot này — ${phan.join("; ") || "không còn video hợp"}. Hãy tải thêm video vào Kho video` : "Hết video chưa đăng — Kho video đang trống. Hãy tải video lên Kho video",
    };
  }
  ung = ung.sort((a, b) => (a.taoLuc < b.taoLuc ? -1 : a.taoLuc > b.taoLuc ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  let chon = ung[0]!;
  let cach = "cũ nhất trước (lần lượt theo thứ tự tải lên)";
  if (y.videoId) cach = "video do người dùng chọn";
  else if (y.cheDo === "ngau_nhien") {
    const x = y.rnd();
    const i = Number.isFinite(x) ? Math.floor(x * ung.length) : 0;
    chon = ung[Math.max(0, Math.min(ung.length - 1, i))]!;
    cach = `ngẫu nhiên trong ${ung.length} video chưa đăng`;
  } else if (y.cheDo === "theo_nhan") cach = `cũ nhất trước trong các video có nhãn “${y.nhan}”`;
  return { ketQua: "chon", video: chon, ghiChu: `${cach}; còn ${ung.length - 1} video chưa đăng sau video này` };
}
